import { useParams, Link, Navigate } from "react-router-dom";

import { CaseStudyCard } from "@/components/case-studies/CaseStudyCard";
import { Card, CardContent } from "@/components/ui/card";
import {
  getCaseStudyBySlug,
  getRelatedCaseStudies,
  formatDuration,
  estimateReadingTime,
} from "@/utils/case-study.utils";

export default function CaseStudyDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const caseStudy = slug ? getCaseStudyBySlug(slug) : undefined;

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  const relatedCaseStudies = getRelatedCaseStudies(caseStudy.metadata.id);
  const readingTime = estimateReadingTime(caseStudy);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <img
          src={caseStudy.media.heroImage.url}
          alt={caseStudy.media.heroImage.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20" />

        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl">
              {/* Breadcrumbs */}
              <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
                <Link to="/" className="hover:text-white transition-colors">
                  Home
                </Link>
                <span>/</span>
                <Link
                  to="/case-studies"
                  className="hover:text-white transition-colors"
                >
                  Case Studies
                </Link>
                <span>/</span>
                <span className="text-white">{caseStudy.overview.title}</span>
              </nav>

              <h1 className="text-5xl font-extrabold text-white mb-4 drop-shadow-lg">
                {caseStudy.overview.title}
              </h1>
              <p className="text-2xl text-white/95 font-medium mb-6 drop-shadow">
                {caseStudy.overview.tagline}
              </p>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-white/90">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span>{caseStudy.client.industryType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    Duration: {formatDuration(caseStudy.overview.duration)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <span>{readingTime} min read</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Sections */}
              {caseStudy.sections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <Card key={section.id}>
                    <CardContent className="p-8">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {section.title}
                      </h2>
                      <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                          {section.content}
                        </p>

                        {/* Render bullet points if present */}
                        {section.bulletPoints &&
                          section.bulletPoints.length > 0 && (
                            <ul className="space-y-4 mt-6">
                              {section.bulletPoints.map((point, idx) => (
                                <li key={idx} className="flex gap-3">
                                  <span className="text-[#B87333] mt-1.5 flex-shrink-0">
                                    •
                                  </span>
                                  <div>
                                    {point.title && (
                                      <strong className="text-gray-900 font-semibold">
                                        {point.title}:
                                      </strong>
                                    )}{" "}
                                    <span className="text-gray-700">
                                      {point.description}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

              {/* Testimonial */}
              {caseStudy.results.testimonial && (
                <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardContent className="p-8">
                    <div className="flex gap-4">
                      <svg
                        className="w-12 h-12 text-[#B87333] flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                      <div>
                        <blockquote className="text-lg text-gray-800 italic mb-4">
                          "{caseStudy.results.testimonial.quote}"
                        </blockquote>
                        <div className="text-sm text-gray-600">
                          <div className="font-semibold text-gray-900">
                            {caseStudy.results.testimonial.author}
                          </div>
                          <div>{caseStudy.results.testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Key Metrics */}
                <Card>
                  <CardContent className="p-6 space-y-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      Key Results
                    </h3>
                    {caseStudy.results.keyMetrics.map((metric, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-3xl flex-shrink-0">
                          {metric.icon}
                        </span>
                        <div>
                          <div className="text-3xl font-bold text-[#9b87f5]">
                            {metric.value}
                            {metric.unit && (
                              <span className="text-xl ml-1">
                                {metric.unit}
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {metric.label}
                          </div>
                          {metric.description && (
                            <div className="text-xs text-gray-600 mt-1">
                              {metric.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Features */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Features
                    </h3>
                    <ul className="space-y-3">
                      {caseStudy.taxonomy.services.map((service) => (
                        <li key={service} className="flex items-start gap-2">
                          <svg
                            className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* CTA */}
                <Card className="bg-gradient-to-br from-[#9b87f5] to-[#B87333] text-white">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-bold mb-3">
                      Ready to automate?
                    </h3>
                    <p className="text-white/90 mb-6">
                      Let's discuss how we can help your business achieve
                      similar results.
                    </p>
                    <a
                      href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-white text-[#B87333] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                    >
                      Schedule a Call
                    </a>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Related Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedCaseStudies.map((related) => (
                <CaseStudyCard
                  key={related.metadata.id}
                  caseStudy={related}
                  variant="compact"
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
